import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as PlantListCompanyByUserGuidActions from './plant-list-company-by-userguid.actions';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError, take, mergeMap } from 'rxjs/operators';
import { of, from, EMPTY } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectPlantListByUserGuid } from './plant-list-company-by-userguid.selectors';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { PlantListCompanyByUserGuidService } from './plant-list-company-by-userguid.service';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { OrderDefaultDataApiService } from '../order-default-data/order-default-data.api.service';
import { ParametersDataApiService } from '../parameters-data/parameters-data.api.service';
import { getCountryParametersScopeType4And5List } from '../parameters-data/parameters-data.utils';

@Injectable()
export class PlantListCompanyByUserGuidEffects {
  constructor(
    private actions$: Actions,
    private plantListService: PlantListCompanyByUserGuidService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private cookieService: CookieService,
    private idb: ManageIdbService,
    private orderDefaultDataApi: OrderDefaultDataApiService,
    private parametersDataApi: ParametersDataApiService,
  ) {}

  loadPlantListCompanyByUserGuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuid),
      switchMap(() => {
        const userGuid = this.cookieService.get(environment.UserGuid).toUpperCase();
        return this.store.select(selectPlantListByUserGuid(userGuid)).pipe(
          take(1),
          switchMap((plants) => {
            if (plants) {
              return of(PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuidSuccess({ userGuid, plants }));
            }
            return from(this.idb.select(IdbCatalogName.Plants)).pipe(
              switchMap((cachedPlants) => {
                if (cachedPlants?.length) {
                  return of(
                    PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuidSuccess({
                      userGuid,
                      plants: cachedPlants,
                    }),
                  );
                }

                return this.plantListService.getPlantListByCompanyByUserGuid().pipe(
                  switchMap((result) =>
                    from(this.idb.savePlants(result)).pipe(
                      catchError((error) => {
                        console.warn('[PlantListCompanyByUserGuidEffects] could not save plants into IDB', error);
                        return of(undefined);
                      }),
                      map(() => result),
                    ),
                  ),
                  map((response) =>
                    PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuidSuccess({
                      userGuid,
                      plants: response,
                    }),
                  ),
                  catchError((e) => {
                    this.alert.error(e?.error?.message || this.translation.pt('views.globals.an-error-occurred'));
                    return of(
                      PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuidFailure({
                        error: e?.error?.message || this.translation.pt('views.globals.an-error-occurred'),
                      }),
                    );
                  }),
                );
              }),
            );
          }),
        );
      }),
    ),
  );

  refreshPlantListCompanyByUserGuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlantListCompanyByUserGuidActions.refreshPlantListCompanyByUserGuid),
      map(() => PlantListCompanyByUserGuidActions.resetPlantListCompanyByUserGuid()),
    ),
  );

  prefetchParametersData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PlantListCompanyByUserGuidActions.loadPlantListCompanyByUserGuidSuccess),
        switchMap(() =>
          this.orderDefaultDataApi.getAll().pipe(
            take(1),
            switchMap((response) => {
              const items: { countryId: number }[] = (response as { items: { countryId: number }[] })?.items || [];
              const countryIds = [...new Set(items.map((item) => item.countryId).filter((id) => !!id))];
              if (countryIds.length === 0) return EMPTY;
              return from(countryIds).pipe(
                mergeMap((countryId) => {
                  const paramList = getCountryParametersScopeType4And5List(countryId);
                  return this.parametersDataApi.getParametersData(paramList).pipe(
                    take(1),
                    catchError(() => EMPTY),
                  );
                }, 3),
              );
            }),
            catchError(() => EMPTY),
          ),
        ),
      ),
    { dispatch: false },
  );
}
